import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtINRFull } from "../lib/api";
import { useToast } from "../lib/toast";
import ArtistCardThumb from "./ArtistCardThumb";
import "./occasion-section.css";
import { track } from "../analytics/tracker";

const FALLBACK_OCCASIONS = [
  { name: "Wedding", slug: "wedding" },
  { name: "Birthday", slug: "birthday" },
  { name: "Corporate", slug: "corporate" },
  { name: "Private Party", slug: "private-party" },
  { name: "Engagement", slug: "engagement" },
  { name: "Anniversary", slug: "anniversary" },
];

const OCCASION_DETAILS = {
  birthday: { icon: "🎂", title: "Birthday party", subtitle: "Kids to milestone birthdays" },
  wedding: { icon: "💍", title: "Wedding / Sangeet", subtitle: "Mehendi, haldi, reception" },
  corporate: { icon: "🏢", title: "Corporate event", subtitle: "Annual day, launches, offsites" },
  "corporate event": { icon: "🏢", title: "Corporate event", subtitle: "Annual day, launches, offsites" },
  "college fest": { icon: "🎓", title: "College fest", subtitle: "Concerts, DJ nights, shows" },
  "private party": { icon: "🎉", title: "Private party", subtitle: "Make it a night to remember" },
  engagement: { icon: "💐", title: "Engagement", subtitle: "Celebrate the big yes" },
  anniversary: { icon: "🥂", title: "Anniversary", subtitle: "Mark another year together" },
};

const occasionDetails = (name) => OCCASION_DETAILS[name.toLowerCase()] || {
  icon: "✨",
  title: name,
  subtitle: "Find artists for your celebration",
};

export default function OccasionSection() {
  const navigate = useNavigate();
  const toast = useToast();
  const [occasions, setOccasions] = useState(FALLBACK_OCCASIONS);
  const [selected, setSelected] = useState(FALLBACK_OCCASIONS[0]);
  const [items, setItems] = useState([]);
  const [priceSummary, setPriceSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addingArtistId, setAddingArtistId] = useState(null);
  const chipRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/catalog/event-types")
      .then(({ data }) => {
        const catalog = (Array.isArray(data) ? data : [])
          .filter((item) => typeof item?.name === "string" && item.name.trim())
          .slice(0, 6)
          .map((item) => ({ name: item.name.trim(), slug: item.slug || item.name }));
        if (cancelled || catalog.length === 0) return;
        setOccasions(catalog);
        setSelected((current) => catalog.find((item) => item.name === current.name) || catalog[0]);
      })
      .catch(() => {
        if (!cancelled) {
          setOccasions(FALLBACK_OCCASIONS);
          setSelected(FALLBACK_OCCASIONS[0]);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setPriceSummary(null);
    setLoadError(false);
    api.get("/homepage/occasion-artists", {
      params: { event_type: selected.name, limit: 8 },
    }).then(({ data }) => {
      if (cancelled) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setPriceSummary(data?.price_summary || null);
    }).catch(() => {
      if (!cancelled) {
        setItems([]);
        setPriceSummary(null);
        setLoadError(true);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [selected]);

  const chooseOccasion = (occasion, index) => {
    setSelected(occasion);
    chipRefs.current[index]?.focus();
  };

  const handleChipKeyDown = (event, index) => {
    let nextIndex;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % occasions.length;
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + occasions.length) % occasions.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = occasions.length - 1;
    else return;
    event.preventDefault();
    chooseOccasion(occasions[nextIndex], nextIndex);
  };

  const searchUrl = `/search?${new URLSearchParams({ event_type: selected.name }).toString()}`;
  const details = occasionDetails(selected.name);
  const isSuggested = items.some((artist) => artist.suggested);
  const lineup = items.slice(0, 4);
  const bookableLineup = lineup.filter((artist) => artist.package_id);
  const lineupStartingTotal = bookableLineup.reduce(
    (sum, artist) => sum + Number(artist.starting_price || 0),
    0
  );

  const addArtistsToCart = (artists) => {
    if (artists.length === 0) {
      toast("No artists with bookable packages are available for this occasion.", "error");
      return;
    }

    setAddingToCart(true);
    setAddingArtistId(artists.length === 1 ? artists[0].id : null);
    try {
      const [primary, ...secondaryArtists] = artists;
      const itemsForCart = secondaryArtists.map((artist) => ({
        artist_id: artist.id,
        artist_name: artist.name,
        artist_photo: artist.image
          ? (/^https?:\/\//.test(artist.image) ? artist.image : `${api.defaults.baseURL}/media/${artist.image}`)
          : null,
        category: artist.category,
        city: artist.city,
        emoji: "",
        package_id: artist.package_id,
        package_name: artist.package_name || "Base Package",
        package_price: artist.starting_price || 0,
        addon_selections: [],
        price_subtotal: artist.starting_price || 0,
      }));
      localStorage.setItem(`bt_event_cart_${primary.id}`, JSON.stringify({
        items: itemsForCart,
        saved_at: Date.now(),
        primary_id: primary.id,
        primary_name: primary.name,
        primary_photo: primary.image
          ? (/^https?:\/\//.test(primary.image) ? primary.image : `${api.defaults.baseURL}/media/${primary.image}`)
          : null,
        primary_category: primary.category,
        primary_city: primary.city,
        from_occasion: selected.name,
      }));
      window.dispatchEvent(new Event("bt-event-cart-changed"));

      const params = new URLSearchParams({
        pkg: primary.package_id,
        event_type: selected.name,
      });
      toast(`${artists.length} artist${artists.length === 1 ? "" : "s"} added to your event.`, "success");
      navigate(`/book/${primary.id}?${params.toString()}`);
    } catch {
      toast("Could not add this lineup to your event. Please try again.", "error");
    } finally {
      setAddingToCart(false);
      setAddingArtistId(null);
    }
  };

  const addLineupToCart = () => addArtistsToCart(bookableLineup);

  return (
    <section className="section occasion-section" data-testid="occasion-section">
      <div className="container">
        <div className="occasion-shell">
          <header className="occasion-intro">
            <span className="occasion-eyebrow landing-section-eyebrow">Plan in 10 seconds</span>
            <h2 className="occasion-title font-serif landing-section-heading">What are you <em>hosting?</em></h2>
          </header>

          <div className="occasion-layout">
            <div className="occasion-chip-list" role="tablist" aria-label="Choose an occasion">
              {occasions.map((occasion, index) => {
                const occasionInfo = occasionDetails(occasion.name);
                const active = selected.name === occasion.name;
                return (
                  <button
                    key={occasion.slug || occasion.name}
                    ref={(node) => { chipRefs.current[index] = node; }}
                    type="button"
                    role="tab"
                    id={`occasion-tab-${index}`}
                    aria-selected={active}
                    aria-controls="occasion-artist-panel"
                    tabIndex={active ? 0 : -1}
                    className={`occasion-choice ${active ? "active" : ""}`}
                    onClick={() => chooseOccasion(occasion, index)}
                    onKeyDown={(event) => handleChipKeyDown(event, index)}
                    data-testid={`occasion-chip-${occasion.slug || index}`}
                  >
                    <span className="occasion-choice-icon" aria-hidden="true">{occasionInfo.icon}</span>
                    <span className="occasion-choice-copy">
                      <span className="occasion-choice-title">{occasionInfo.title}</span>
                      <span className="occasion-choice-subtitle">{occasionInfo.subtitle}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              id="occasion-artist-panel"
              className="occasion-artist-panel"
              role="tabpanel"
              aria-labelledby={`occasion-tab-${occasions.findIndex((occasion) => occasion.name === selected.name)}`}
              aria-live="polite"
              data-testid="occasion-artist-panel"
            >
              <div className="occasion-panel-heading">
                <h3>{isSuggested ? "Suggested" : "Popular"} for {details.title}</h3>
                {priceSummary && (
                  <p className="occasion-price-summary" data-testid="occasion-price-summary">
                    Typical starting prices: {fmtINRFull(priceSummary.min)}–{fmtINRFull(priceSummary.max)}
                  </p>
                )}
              </div>

              {loading ? (
                <div className="occasion-artist-list" aria-label={`Loading artists for ${selected.name}`}>
                  {[...Array(3)].map((_, index) => (
                    <div key={index} className="occasion-skeleton-row" aria-hidden="true">
                      <span className="occasion-skeleton-avatar" />
                      <span className="occasion-skeleton-lines"><i /><i /></span>
                      <span className="occasion-skeleton-price" />
                    </div>
                  ))}
                </div>
              ) : loadError ? (
                <div className="occasion-empty" role="alert" data-testid="occasion-load-error">
                  <p>Could not load artists for {selected.name}. Please try again.</p>
                </div>
              ) : items.length === 0 ? (
                <div className="occasion-empty" data-testid="occasion-empty">
                  <p>No artists yet for {selected.name}</p>
                  <Link to={searchUrl}>Explore artists on BookTalent →</Link>
                </div>
              ) : (
                <div className="occasion-artist-list" data-testid="occasion-artist-row">
                  {lineup.map((artist) => (
                    <div
                      key={artist.id}
                      className="occasion-artist-row"
                      data-testid={`occasion-artist-${artist.id}`}
                    >
                      <Link className="occasion-artist-profile" to={`/artist/${artist.id}`} onClick={() => track("artist_card_click", { artist_id: artist.id, source: "home_section" })}>
                        <ArtistCardThumb
                          artist={{ ...artist, profile_image: artist.image }}
                          className="occasion-artist-avatar"
                          placeholder={<span>{(artist.name || "?").slice(0, 2).toUpperCase()}</span>}
                        />
                        <span className="occasion-artist-copy">
                          <span className="occasion-artist-name">{artist.name}</span>
                          <span className="occasion-artist-meta">
                            {[artist.category, artist.city].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        <span className="occasion-artist-price">
                          <strong>{fmtINRFull(artist.starting_price)}</strong>
                          <small>starting from</small>
                        </span>
                      </Link>
                      <button
                        type="button"
                        className="occasion-add-one"
                        onClick={() => addArtistsToCart([artist])}
                        disabled={addingToCart || !artist.package_id}
                        data-testid={`occasion-add-${artist.id}`}
                      >
                        {addingArtistId === artist.id ? "Adding…" : "Add to cart"}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!loading && !loadError && items.length > 0 && (
                <div className="occasion-bundle-footer">
                  <div className="occasion-bundle-total">
                    <strong data-testid="occasion-lineup-total">{fmtINRFull(lineupStartingTotal)}</strong>
                    <span>Starting total for {bookableLineup.length} artist{bookableLineup.length === 1 ? "" : "s"}</span>
                    <small>Final price depends on selected packages and add-ons.</small>
                  </div>
                  <button
                    type="button"
                    className="occasion-add-all"
                    onClick={addLineupToCart}
                    disabled={addingToCart || bookableLineup.length === 0}
                    data-testid="occasion-add-all"
                  >
                    {addingToCart ? "Adding lineup…" : `Add ${bookableLineup.length} to cart →`}
                  </button>
                </div>
              )}

              <div className="occasion-panel-links">
                <Link className="occasion-view-all" to={searchUrl}>
                  View all {selected.name} artists →
                </Link>
                <Link className="occasion-planner-link" to="/planner">
                  Need help planning? Try the Event Planner →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
